import { EpisodeStatus, MergePreview } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';

// ─────────────────────────────────────────────────────────────
// Legacy status mapping
// ─────────────────────────────────────────────────────────────

type LegacyStatus = 'draft' | 'processing' | 'ready' | 'failed';

const LEGACY_STATUS_MAP: Record<LegacyStatus, EpisodeStatus> = {
  'draft': EpisodeStatus.SCRIPT_READY,
  'processing': EpisodeStatus.GENERATING_AUDIO,
  'ready': EpisodeStatus.AUDIO_READY,
  'failed': EpisodeStatus.AUDIO_FAILED,
};

/** Normalize legacy statuses to new enum values */
export const normalizeStatus = (status: string): EpisodeStatus => {
  if (Object.values(EpisodeStatus).includes(status as EpisodeStatus)) {
    return status as EpisodeStatus;
  }
  if (status in LEGACY_STATUS_MAP) {
    return LEGACY_STATUS_MAP[status as LegacyStatus];
  }
  return EpisodeStatus.GENERATING_AUDIO;
};

// ─────────────────────────────────────────────────────────────
// Merge sequence
// ─────────────────────────────────────────────────────────────

/** Merge sequence item types for the preview */
export interface MergeSequenceItem {
  type: 'intro' | 'clip' | 'commercial' | 'outro';
  label: string;
  audioPath: string;
  clip?: Clip;
  commercialIndex?: number;
  duration?: number | null;
}

/**
 * Build the merge sequence array showing the exact order of audio files
 * that will be merged: intro -> clips with interleaved commercials -> outro
 */
export const buildMergeSequence = (
  clips: Clip[],
  mergePreview: MergePreview
): MergeSequenceItem[] => {
  const sequence: MergeSequenceItem[] = [];

  // 1. Add intro if configured
  if (mergePreview.introPath?.trim()) {
    sequence.push({
      type: 'intro',
      label: 'Intro',
      audioPath: mergePreview.introPath,
      duration: mergePreview.introDuration,
    });
  }

  // 2. Build commercials array with durations
  const commercials = [
    { path: mergePreview.commercial1Path, duration: mergePreview.commercial1Duration },
    { path: mergePreview.commercial2Path, duration: mergePreview.commercial2Duration },
    { path: mergePreview.commercial3Path, duration: mergePreview.commercial3Duration },
    { path: mergePreview.commercial4Path, duration: mergePreview.commercial4Duration },
  ].filter((c): c is { path: string; duration: number | null } => !!c.path && c.path.trim() !== '');

  // 3. Interleave clips with commercials
  const sortedClips = [...clips].sort((a, b) => a.position - b.position);
  sortedClips.forEach((clip, index) => {
    if (clip.audioPath) {
      sequence.push({
        type: 'clip',
        label: clip.articleTitle,
        audioPath: clip.audioPath,
        clip,
        duration: clip.duration,
      });

      // Add commercial after each clip (except last) - only if commercials exist
      if (index < sortedClips.length - 1 && commercials.length > 0) {
        const commIndex = index % commercials.length;
        sequence.push({
          type: 'commercial',
          label: `Commercial`,
          audioPath: commercials[commIndex].path!,
          commercialIndex: commIndex + 1,
          duration: commercials[commIndex].duration,
        });
      }
    }
  });

  // 4. Add outro if configured
  if (mergePreview.outroPath?.trim()) {
    sequence.push({
      type: 'outro',
      label: 'Outro',
      audioPath: mergePreview.outroPath,
      duration: mergePreview.outroDuration,
    });
  }

  return sequence;
};

// ─────────────────────────────────────────────────────────────
// Formatting & date helpers
// ─────────────────────────────────────────────────────────────

/** Format total duration in seconds to MM:SS */
export const formatTotalDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Convert various date formats to Date object.
 * Handles: Date, ISO string, number (ms), Firestore Timestamp objects.
 * Returns null for invalid dates to prevent RangeError in date formatting.
 */
export const toDate = (value: unknown): Date | null => {
  if (!value) return null;

  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'object') {
    // Handle Firestore Timestamp objects (from WebSocket JSON)
    const obj = value as Record<string, unknown>;
    const seconds = (obj._seconds ?? obj.seconds) as number | undefined;
    if (typeof seconds === 'number') {
      date = new Date(seconds * 1000);
    }
  }

  // Validate the date - return null if invalid to prevent RangeError
  if (date && !isNaN(date.getTime())) {
    return date;
  }
  return null;
};
